import type { Validations, Violation } from "@credit-acceptance/step6-bff-client";

import { stripFieldPrefix, type TranslatedViolations, translateViolations } from "../../../serverViolations";
import type { ContractVehicleInput } from "./ContractVehicleInformation.schema";
import { isInsuranceViolation } from "./Insurance.violations";

// ---------------------------------------------------------------------------
// ContractVehicle.violations
//
// Maps `vehicle.validations` onto Contract Vehicle's RHF fields. Both the
// write-model (VehicleSaveRequestV2) and read-model (VehicleOnDealV2) names are
// accepted, bare or under `vehicle.` and/or `vehicleOnDeal.`; only the bare
// write-model names are fixture-confirmed. The odometer change
// reason has no input of its own, so its findings land on the mileage it
// explains. CPO fields only render for a CPO vehicle, so on any other vehicle
// they fall through to `unmapped`. `insurance.*` findings belong to the
// Insurance subsection and are skipped here.
// ---------------------------------------------------------------------------

type ContractVehiclePath = keyof ContractVehicleInput;

const VEHICLE_FIELD_MAP: Record<string, ContractVehiclePath> = {
  msrp: "totalSellingPrice",
  odometerDiscMileage: "odometerDiscMileage",
  odometerMileage: "odometerDiscMileage",
  odometerChangeReasonPk: "odometerDiscMileage",
  cpo: "isCertifiedPreOwned",
  "certifiedPreOwnedDetails.isVerified": "isCertifiedPreOwned",
  cpoDate: "cpoDate",
  "certifiedPreOwnedDetails.certificationDate": "cpoDate",
};

const CPO_ONLY_PATHS = new Set<ContractVehiclePath>(["isCertifiedPreOwned", "cpoDate"]);

/** Non-blocking finding: the server saved the date and only warns. */
const CPO_DATE_EXCEEDED_CODE = "CPO_DATE_EXCEEDED";

/**
 * @param {Violation} violation - A `vehicle.validations` entry.
 * @returns {boolean} Whether it belongs to Contract Vehicle rather than Insurance.
 */
function isVehicleViolation(violation: Violation): boolean {
  return !isInsuranceViolation(violation);
}

export interface TranslatedVehicleViolations extends TranslatedViolations<ContractVehiclePath> {
  /** Non-blocking findings, shown beside the field instead of as an error. */
  warnings: Map<ContractVehiclePath, string>;
}

/**
 * Translates the vehicle leg's validations into field errors, non-blocking warnings, and unmapped findings.
 * @param {Validations | undefined} validations - `vehicle.validations` from the dashboard or a save response.
 * @param {{ isCertifiedPreOwned: boolean }} context - Whether the CPO fields are rendered.
 * @param {boolean} context.isCertifiedPreOwned - The form's current CPO flag.
 * @returns {TranslatedVehicleViolations} The translated findings.
 */
export function translateVehicleViolations(
  validations: Validations | undefined,
  context: { isCertifiedPreOwned: boolean }
): TranslatedVehicleViolations {
  const warnings = new Map<ContractVehiclePath, string>();
  const resolve = (field: string | undefined) => {
    const path = field
      ? VEHICLE_FIELD_MAP[stripFieldPrefix(stripFieldPrefix(field, ["vehicle"]), ["vehicleOnDeal"])]
      : undefined;
    return path && (context.isCertifiedPreOwned || !CPO_ONLY_PATHS.has(path)) ? path : undefined;
  };

  const businessRuleViolations = validations?.businessRuleViolations?.filter(isVehicleViolation);
  const exceeded = (businessRuleViolations ?? []).filter(
    (violation) => violation.code === CPO_DATE_EXCEEDED_CODE && resolve(violation.field) === "cpoDate"
  );
  for (const violation of exceeded) {
    warnings.set("cpoDate", violation.message);
  }

  const translated = translateViolations(
    {
      requiredViolations: validations?.requiredViolations?.filter(isVehicleViolation),
      businessRuleViolations: businessRuleViolations?.filter((violation) => !exceeded.includes(violation)),
    },
    (violation) => resolve(violation.field)
  );
  return { ...translated, warnings };
}
